import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslate } from '../../hooks/useTranslate';
import { getTrainingVideoUrl } from '../../services/trainingVideoService';
import { FaInfoCircle, FaExternalLinkAlt } from 'react-icons/fa';
import type { TrainingVideo } from '../../types/training';

interface ContextualHelpLinkProps {
    videoId: string;
    /** 'icon' for dark-on-light pages, 'icon-light' for white-on-dark modal headers */
    variant?: 'icon' | 'icon-light';
}

const ContextualHelpLink: React.FC<ContextualHelpLinkProps> = ({ videoId, variant = 'icon' }) => {
    const { t } = useTranslate();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [videoMeta, setVideoMeta] = useState<TrainingVideo | null>(null);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const isLight = variant === 'icon-light';

    // Fetch video metadata + signed URL when popover opens
    useEffect(() => {
        if (!isPopoverOpen) return;

        let cancelled = false;
        setLoading(true);

        getTrainingVideoUrl(videoId)
            .then(({ url, video }) => {
                if (!cancelled) {
                    setVideoUrl(url);
                    setVideoMeta(video);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [isPopoverOpen, videoId]);

    // Close on outside click
    useEffect(() => {
        if (!isPopoverOpen) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsPopoverOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isPopoverOpen]);

    // Close on Escape
    useEffect(() => {
        if (!isPopoverOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setIsPopoverOpen(false);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isPopoverOpen]);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setIsPopoverOpen(prev => !prev);
    }, []);

    const handleViewVideo = useCallback(() => {
        if (videoUrl) {
            const title = videoMeta?.title ?? 'Training Video';
            const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><title>${title.replace(/[<>&"]/g, '')}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{background:#000;display:flex;align-items:center;justify-content:center;height:100vh}
video{max-width:100%;max-height:100vh}</style></head>
<body><video controls controlsList="nodownload" disablePictureInPicture autoplay oncontextmenu="return false" src="${videoUrl}"></video></body></html>`;
            const blob = new Blob([html], { type: 'text/html' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank', 'noopener');
            setIsPopoverOpen(false);
        }
    }, [videoUrl, videoMeta]);

    return (
        <div ref={containerRef} className="relative inline-flex">
            <button
                type="button"
                onClick={handleToggle}
                className={`group relative inline-flex items-center justify-center rounded-full transition-all duration-200 ${isLight
                    ? 'w-8 h-8 bg-white/10 hover:bg-white/25 text-white/70 hover:text-white'
                    : 'w-7 h-7 text-[#D31145]/60 hover:text-[#D31145] hover:bg-red-50'
                    }`}
                aria-label={t('Video help')}
                aria-expanded={isPopoverOpen}
                title={t('Video help')}
            >
                <FaInfoCircle className={isLight ? 'w-[18px] h-[18px]' : 'w-4 h-4'} />
            </button>

            {isPopoverOpen && (
                <div
                    className={`absolute top-full mt-2 right-0 w-72 rounded-xl shadow-xl border z-[1060] ${isLight
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-200 text-gray-800'
                        }`}
                    role="dialog"
                    aria-label={t('Video help')}
                >
                    {/* Arrow */}
                    <div className={`absolute -top-2 right-3 w-4 h-4 rotate-45 border-l border-t ${isLight
                        ? 'bg-gray-800 border-gray-700'
                        : 'bg-white border-gray-200'
                        }`} />

                    <div className="relative p-4 space-y-3">
                        {loading ? (
                            <div className="flex items-center gap-2 py-2">
                                <div className="w-4 h-4 border-2 border-current/20 border-t-current rounded-full animate-spin" />
                                <span className="text-sm opacity-70">{t('Loading...')}</span>
                            </div>
                        ) : videoMeta ? (
                            <>
                                <div>
                                    <h3 className={`text-sm font-semibold leading-snug ${isLight ? 'text-white' : 'text-gray-900'}`}>
                                        {videoMeta.title}
                                    </h3>
                                    <p className={`text-xs mt-1.5 leading-relaxed ${isLight ? 'text-gray-300' : 'text-gray-500'}`}>
                                        {videoMeta.description}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleViewVideo}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors duration-150 bg-[#D31145] text-white hover:bg-[#b50e3a]"
                                >
                                    {t('View Video')}
                                    <FaExternalLinkAlt className="w-3 h-3" />
                                </button>
                            </>
                        ) : (
                            <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-gray-500'}`}>
                                {t('Video not available')}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContextualHelpLink;
